import { ComponentProps, useMemo } from "react";
import { useRouter } from "next/router";
import { SectionScaffold } from "./SectionScaffold";
import { Interpolation, Theme, css, useTheme } from "@emotion/react";
import { Empty } from "components/core/Empty";
import { IconButton } from "components/core/buttons/IconButton";
import { ContentGridMixContentQuery } from "components/domain/contents/ContentGridMixContentQuery";
import { ContentGridQuery } from "components/domain/contents/ContentGridQuery";
import { useAuth } from "hooks/useAuth";
import { LIMIT } from "utils/buildQueryVariables";
import {
  MIX_CONTENT_TAG_ENUM_KOREAN,
  ORDERED_MIX_CONTENT_TAG_ENUM,
} from "utils/enum";
import { useMixArchives } from "../hooks/useMixArchives";
import { ContentSection } from "types/app";

export const MixedSection = ({
  contentSection,
  variables,
  limit = LIMIT.default,
  ...props
}: Omit<
  ComponentProps<
    typeof ContentGridQuery<MixContentQuery, MixContentQueryVariables>
  >,
  "useQuery" | "extractor" | "updateQuery"
> & {
  css?: Interpolation<Theme>;
  contentSection: ContentSection;
  variables: MixContentQueryVariables;
}) => {
  const theme = useTheme();
  const router = useRouter();
  const { me } = useAuth();
  const isGuest = !me;

  const { archives, archiveTagEnums, archivesLoading } = useMixArchives();

  const tag = useMemo(
    () =>
      ORDERED_MIX_CONTENT_TAG_ENUM.find((tag) => tag === router.query.tag) ??
      null,
    [router],
  );

  const tagDisplayName = useMemo(() => {
    // Handle campaign-specific display names
    const campaignTag = router.query.tag;
    // if (campaignTag === "trial_1") {
    //   return "미래에셋증권";
    // }
    if (!tag) return null;
    return MIX_CONTENT_TAG_ENUM_KOREAN[tag];
  }, [tag, router.query.tag]);

  return (
    <SectionScaffold {...props}>
      <SectionScaffold.Header>
        <SectionScaffold.Title>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <span
              css={css`
                white-space: nowrap;
                overflow: hidden;
                color: ${theme.palette.text.primary};
              `}
            >
              콘텐츠 큐레이션
            </span>
            <IconButton
              name="MoreHoriz"
              size="medium"
              color="grey"
              variant="background"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                e.stopPropagation();
                router.push(
                  {
                    ...router,
                    hash: me ? "#interest-tags" : "#signin",
                  },
                  undefined,
                  { scroll: false },
                );
              }}
              css={css`
                flex-shrink: 0;
                display: none;
                @media (max-width: 1024px) {
                  display: block;
                }
              `}
            />
          </div>
          {tag !== null && (
            <span
              css={css`
                margin-left: 12px;
                color: ${theme.palette.text.placeholder};
                @media (max-width: 1024px) {
                  display: none;
                }
              `}
            >
              #{tagDisplayName}
            </span>
          )}
        </SectionScaffold.Title>
      </SectionScaffold.Header>

      <ContentGridMixContentQuery
        type="mixed"
        contentSection={contentSection}
        limit={limit}
        variables={variables}
        archives={archives}
        archiveTagEnums={archiveTagEnums}
        archivesLoading={archivesLoading}
        isGuest={isGuest}
        empty={
          <Empty
            imgurl="/img/content-empty.png"
            mainText="추천할 만한 콘텐츠를 열심히 찾고 있어요."
            subText="조금만 기다려 주세요!"
          />
        }
      />
    </SectionScaffold>
  );
};
